import { PRE_SHIFT_ORIENTATION_MINIMUM_LEAD_TIME_IN_MINUTES } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { isAfter, isBefore, subMinutes } from "date-fns";

interface BookedShift {
  start: string;
  workplaceId: string;
}

export interface PreShiftOrientationEvaluation {
  hasPreShiftOrientation: boolean;
  adjustedStart?: Date;
  orientationLengthInMinutes?: number;
}

/**
 * Time-independent portion of the evaluation. Intended for list views that
 * memoize the filtering work and apply the lead-time fallback via
 * {@link applyPreShiftOrientationLeadTime} on every render. Everyday callers
 * should use {@link hasPreShiftOrientation}, which composes both steps.
 */
export interface PreShiftOrientationBaseEvaluation {
  hasPreShiftOrientation: boolean;
  workplaceOrientationLengthInMinutes?: number;
  originalShiftStart?: Date;
}

/**
 * Determines whether an open shift should be displayed as a "pre-shift
 * orientation shift". Does not account for the lead-time fallback.
 *
 * The worker arrives early only when ALL of the following are true:
 *   1. The workplace has `preShiftOrientationLengthInMinutes` configured.
 *   2. The worker does NOT have an orientation credential for this workplace.
 *   3. The worker does NOT have an active `PreShiftOrientation` record at this
 *      workplace (another booked shift already holds the designation).
 *   4. The worker does NOT have an earlier booked shift at this workplace
 *      (backstop for async job races and pre-launch bookings).
 */
export function evaluatePreShiftOrientationBase(params: {
  workplaceId: string;
  shiftStart: string;
  preShiftOrientationLengthInMinutes?: number;
  workplacesWithOrientationCredentials: Set<string>;
  workplacesWithUpcomingPreShiftOrientation: Map<string, Date>;
  bookedShifts: BookedShift[];
}): PreShiftOrientationBaseEvaluation {
  const {
    workplaceId,
    shiftStart,
    preShiftOrientationLengthInMinutes,
    workplacesWithOrientationCredentials,
    workplacesWithUpcomingPreShiftOrientation,
    bookedShifts,
  } = params;

  if (!isDefined(preShiftOrientationLengthInMinutes) || preShiftOrientationLengthInMinutes <= 0) {
    return { hasPreShiftOrientation: false };
  }

  if (workplacesWithOrientationCredentials.has(workplaceId)) {
    return { hasPreShiftOrientation: false };
  }

  const shiftStartDate = new Date(shiftStart);

  // Only suppress if the existing pre-shift orientation/booked shift starts earlier than the current shift.
  // If the current shift starts earlier, the pre-shift orientation will be transferred
  // to it upon booking, so we should still show the orientation card.
  const existingOrientationStart = workplacesWithUpcomingPreShiftOrientation.get(workplaceId);
  if (isDefined(existingOrientationStart) && !isAfter(existingOrientationStart, shiftStartDate)) {
    return { hasPreShiftOrientation: false };
  }

  const hasEarlierBookedShiftAtWorkplace = bookedShifts.some(
    (bookedShift) =>
      bookedShift.workplaceId === workplaceId &&
      !isAfter(new Date(bookedShift.start), shiftStartDate)
  );

  if (hasEarlierBookedShiftAtWorkplace) {
    return { hasPreShiftOrientation: false };
  }

  return {
    hasPreShiftOrientation: true,
    workplaceOrientationLengthInMinutes: preShiftOrientationLengthInMinutes,
    originalShiftStart: shiftStartDate,
  };
}

/**
 * Suppresses the orientation when the adjusted start would fall within
 * {@link PRE_SHIFT_ORIENTATION_MINIMUM_LEAD_TIME_IN_MINUTES} of `currentTime`,
 * so the shift start is not moved earlier than the worker can reach it. The
 * backend still tracks the orientation record so subsequent shifts at the
 * workplace don't require one.
 */
export function applyPreShiftOrientationLeadTime(
  base: PreShiftOrientationBaseEvaluation,
  currentTime: Date
): PreShiftOrientationEvaluation {
  if (
    !base.hasPreShiftOrientation ||
    !isDefined(base.originalShiftStart) ||
    !isDefined(base.workplaceOrientationLengthInMinutes)
  ) {
    return { hasPreShiftOrientation: false };
  }

  const { originalShiftStart, workplaceOrientationLengthInMinutes } = base;
  const adjustedStart = subMinutes(originalShiftStart, workplaceOrientationLengthInMinutes);
  const isTooCloseToNow = isBefore(
    subMinutes(adjustedStart, PRE_SHIFT_ORIENTATION_MINIMUM_LEAD_TIME_IN_MINUTES),
    currentTime
  );

  if (isTooCloseToNow) {
    return { hasPreShiftOrientation: false };
  }

  return {
    hasPreShiftOrientation: true,
    adjustedStart,
    orientationLengthInMinutes: workplaceOrientationLengthInMinutes,
  };
}

/**
 * Evaluates the pre-shift orientation end-to-end, including the lead-time
 * fallback against the current time. Use this from per-render call sites. List
 * views that memoize the filtering work should use
 * {@link evaluatePreShiftOrientationBase} plus {@link applyPreShiftOrientationLeadTime}
 * so the lead-time transition stays responsive without recomputing the filters.
 */
export function hasPreShiftOrientation(params: {
  workplaceId: string;
  shiftStart: string;
  preShiftOrientationLengthInMinutes?: number;
  workplacesWithOrientationCredentials: Set<string>;
  workplacesWithUpcomingPreShiftOrientation: Map<string, Date>;
  bookedShifts: BookedShift[];
}): PreShiftOrientationEvaluation {
  return applyPreShiftOrientationLeadTime(evaluatePreShiftOrientationBase(params), new Date());
}
